import { DialogActions, DialogContent, Grid, Stack } from "@mui/material";
import { MutationStatus } from "@tanstack/react-query";
import { Form as FormikForm, FormikProvider, useFormik } from "formik";
import { Building2, Save } from "lucide-react";
import React, { FC, useMemo } from "react";
import * as yup from "yup";

// COMPONENTS:

import FormikAutocomplete from "components/forms/FormikAutocomplete";
import FormikNumberField from "components/forms/FormikNumberField";
import FormikTextField from "components/forms/FormikTextField";
import FormSection from "components/forms/FormSection";
import UltimateButton from "components/UltimateButton";

export type OrganisationFormValues = {
  name: string;
  email: string;
  leadSource: string;
  brand: string | null;
  partnerPayout: {
    active: boolean;
    type: "they-invoice" | "percentage";
    value: number | null;
  };
};

interface IOrganisationForm {
  handleSubmit: (values: OrganisationFormValues) => void;
  handleClose: () => void;
  initialValues?: OrganisationFormValues;
  submitStatus?: MutationStatus;
}

const OrganisationForm: FC<IOrganisationForm> = ({
  handleSubmit,
  handleClose,
  initialValues = {
    name: "",
    email: "",
    leadSource: "",
    brand: null,
    partnerPayout: {
      active: false,
      type: "they-invoice",
      value: null,
    },
  },
  submitStatus,
}) => {
  // const { formatMessage } = useIntl();
  const schema = yup.object({
    name: yup.string().required(),
    email: yup.string().email("Wrong email format").required(),
    leadSource: yup.string().required(),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: handleSubmit,
  });

  const { values, setFieldValue } = formik;

  const partnerPayoutType = useMemo(() => {
    if (!values.brand) {
      return [{ label: "They Invoice", value: "they-invoice" }];
    }

    return [
      { label: "They Invoice", value: "they-invoice" },
      {
        label: "Percentage",
        value: "percentage",
      },
    ];
  }, [values.brand]);
  return (
    <FormikProvider value={formik}>
      <FormikForm>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <FormSection
              title="Organisation Details"
              description="Company information and partner settings."
              Icon={Building2}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormikTextField name="name" label="Company Name" />
                </Grid>
                <Grid item xs={12}>
                  <FormikTextField name="email" label="Company Email" />
                </Grid>
                <Grid item xs={12}>
                  <FormikTextField name="leadSource" label="Lead Source" />
                </Grid>
                <Grid item xs={12}>
                  <FormikAutocomplete
                    name="brand"
                    label="Brand"
                    options={[
                      {
                        label: "XS Capital Solutions",
                        value: "XS Capital Solutions",
                      },
                      {
                        label: "Green Leaf Money",
                        value: "Green Leaf Money",
                      },
                      {
                        label: "GLM Enterprises, LTD",
                        value: "GLM Enterprises, LTD",
                      },
                      {
                        label: "CBFsg",
                        value: "CBFsg",
                      },
                      {
                        label: "CBF",
                        value: "CBF",
                      },
                    ]}
                    onChange={(e, value) => {
                      if (value?.value) {
                        setFieldValue("brand", value.value);
                      } else {
                        setFieldValue("brand", null);
                        setFieldValue("partnerPayout.type", "they-invoice");
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormikAutocomplete
                    name="partnerPayout.type"
                    label="Referral Partner Payout"
                    options={partnerPayoutType}
                  />
                </Grid>
                {values.partnerPayout.type !== "they-invoice" && (
                  <Grid item xs={12}>
                    <FormikNumberField
                      name="partnerPayout.value"
                      label="Referral Partner Payout Value"
                      suffix="%"
                    />
                  </Grid>
                )}
              </Grid>
            </FormSection>
          </Stack>
        </DialogContent>

        <DialogActions>
          <UltimateButton
            type="submit"
            text="Save"
            Icon={
              Save as React.ComponentType<{
                size?: number;
                className?: string;
                style?: React.CSSProperties;
              }>
            }
            loading={submitStatus === "loading"}
            sx={{
              minWidth: 150,
              py: 1.5,
            }}
          />
        </DialogActions>
      </FormikForm>
    </FormikProvider>
  );
};

export default OrganisationForm;
